import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { ObjectDetailsTab } from './ObjectDetailsTab'
import { FloorPlanTab } from './FloorPlanTab'
import { RecoveryInformationTab } from './RecoveryInformationTab'
import { RecoveryDocumentVideoTab } from './RecoveryDocumentVideoTab'
import { RunbookTab } from './RunbookTab'
import { ObjectPictureTab } from './ObjectPictureTab'
import { useCreateObject, useUpdateObject, useObject } from '../hooks'
import { createObjectSchema, updateObjectSchema, type ObjectFormData } from '../schema'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import type { Object as ObjectEntity } from '@/types/entities'
import { Map, FileText, Video, BookOpen, Image, List } from 'lucide-react'

interface ObjectFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    object: ObjectEntity | null
    objectService?: 'Object' | 'Service'
}

export function ObjectFormDialog({ open, onOpenChange, object, objectService = 'Object' }: ObjectFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!object
    const createObject = useCreateObject()
    const updateObject = useUpdateObject()
    const [activeTab, setActiveTab] = useState('details')
    const isService = !isEditing && objectService === 'Service'
    const showRunbookTab = !isEditing
    
    // Fetch full object data when editing (list objects don't have all fields)
    const { data: fullObjectData, isLoading: isLoadingObject } = useObject(
        isEditing && object?.objectID ? object.objectID : null
    )
    
    // Use full object data if available, otherwise fall back to passed object
    const objectToUse = isEditing && fullObjectData ? fullObjectData : object

    const form = useForm<ObjectFormData>({
        resolver: zodResolver(isEditing ? updateObjectSchema : createObjectSchema) as any,
        mode: 'onChange',
        defaultValues: {
            ObjectService: 'Object',
            ObjectID: '',
            Description: null,
            LayerID: null,
            ResponsibleTeam: null,
            RPO: null,
            RTO: null,
            Location: null,
            ManagementOnly: 0,
            BackupSolution: null,
            Datastore: 0,
            ApplicationEncryption: 1,
            Analytics: 'Yes',
            Icon: null,
            Technology: null,
            FloorPlanImage: null,
            FloorPlanMarker: null,
            Instructions: null,
            RecoveryDocument: null,
            RecoveryVideo: null,
            MasterRunbook: null,
            RunbookTasks: [],
            ObjectImage: null,
        },
    })

    // Reset form when dialog opens/closes or object changes
    useEffect(() => {
        if (open) {
            // Don't reset if we're still loading the full object data
            if (isEditing && isLoadingObject) {
                return
            }
            
            if (objectToUse) {
                // RPO/RTO dropdown options
                const rpoRtoOptions = ['15', '30', '60', '120', '240', '480', '1440']
                
                // Convert RPO: if it matches a dropdown option, use string; otherwise keep as number
                let rpoValue: string | number | null = null
                if (objectToUse.rpo !== null && objectToUse.rpo !== undefined) {
                    const rpoStr = objectToUse.rpo.toString()
                    rpoValue = rpoRtoOptions.includes(rpoStr) ? rpoStr : Number(objectToUse.rpo)
                }
                
                // Convert RTO: if it matches a dropdown option, use string; otherwise keep as number
                let rtoValue: string | number | null = null
                if (objectToUse.rto !== null && objectToUse.rto !== undefined) {
                    const rtoStr = objectToUse.rto.toString()
                    rtoValue = rpoRtoOptions.includes(rtoStr) ? rtoStr : Number(objectToUse.rto)
                }
                
                console.log('Resetting form with object data:', objectToUse)
                
                form.reset({
                    ObjectService: objectToUse.objectService || 'Object',
                    ObjectID: objectToUse.objectID || '',
                    Description: objectToUse.objectDescription || null,
                    LayerID: objectToUse.layerID,
                    ResponsibleTeam: objectToUse.responsibleTeam !== null && objectToUse.responsibleTeam !== undefined ? Number(objectToUse.responsibleTeam) : null,
                    RPO: rpoValue,
                    RTO: rtoValue,
                    Location: objectToUse.locationID || null,
                    ManagementOnly: objectToUse.managementOnly || 0,
                    BackupSolution: objectToUse.backupSolution || null,
                    Datastore: objectToUse.datastore || 0,
                    ApplicationEncryption: objectToUse.applicationEncryption === '1' || objectToUse.applicationEncryption === 1 ? 1 : 0,
                    Analytics: objectToUse.analytics === 'Yes' || objectToUse.analytics === 'yes' ? 'Yes' : 'No',
                    Icon: objectToUse.iconsId != null && objectToUse.iconsId !== 0 ? String(objectToUse.iconsId) : null,
                    Technology: objectToUse.technology || null,
                    FloorPlanImage: null,
                    FloorPlanMarker: null,
                    Instructions: objectToUse.instructions || null,
                    RecoveryDocument: null,
                    RecoveryVideo: null,
                    MasterRunbook: objectToUse.masterRunbook || null,
                    RunbookTasks: [],
                    ObjectImage: null,
                })
            } else {
                form.reset({
                    ObjectService: objectService,
                    ObjectID: '',
                    Description: null,
                    LayerID: null,
                    ResponsibleTeam: null,
                    RPO: null,
                    RTO: null,
                    Location: null,
                    ManagementOnly: 0,
                    BackupSolution: null,
                    Datastore: 0,
                    ApplicationEncryption: 1,
                    Analytics: 'Yes',
                    Icon: null,
                    Technology: null,
                    FloorPlanImage: null,
                    FloorPlanMarker: null,
                    Instructions: null,
                    RecoveryDocument: null,
                    RecoveryVideo: null,
                    MasterRunbook: null,
                    RunbookTasks: [],
                    ObjectImage: null,
                })
            }
            setActiveTab('details')
        }
    }, [open, objectToUse, form, isEditing, isLoadingObject])

    const handleSubmit = async (data: ObjectFormData) => {
        try {
            // Recalculate TaskOffset for all runbook tasks before submission
            let runbookTasks = data.RunbookTasks || []
            if (runbookTasks.length > 0) {
                runbookTasks = calculateRunbookTimes(runbookTasks)
            }
            
            // Transform form data to API format
            const submitData: any = {
                ObjectService: data.ObjectService || objectService,
                ObjectID: data.ObjectID,
                Description: data.Description || null,
                LayerID: data.LayerID || null,
                ResponsibleTeam: data.ResponsibleTeam || null,
                RPO: isService ? null : (data.RPO || null),
                RTO: data.RTO || null,
                Location: isService ? null : (data.Location || null),
                ManagementOnly: isService ? null : (data.ManagementOnly || 0),
                BackupSolution: isService ? null : (data.BackupSolution || null),
                Datastore: isService ? null : (data.Datastore || 0),
                ApplicationEncryption: isService ? null : (data.ApplicationEncryption || 0),
                Analytics: isService ? null : (data.Analytics || 'No'),
                Technology: isService ? null : (data.Technology || null),
                Icon: data.Icon || null,
                Instructions: data.Instructions || null,
                RecoveryDocument: data.RecoveryDocument || null,
                RecoveryVideo: data.RecoveryVideo || null,
                MasterRunbook: data.MasterRunbook || null,
                RunbookTasks: runbookTasks,
            }

            if (isEditing && objectToUse?.objectID) {
                await updateObject.mutateAsync({
                    id: objectToUse.objectID,
                    data: submitData,
                })
            } else {
                await createObject.mutateAsync(submitData as any)
            }
            onOpenChange(false)
        } catch (error: any) {
            console.error('Form submit error:', error)
            console.error('Error response:', error?.response?.data)
            console.error('Error message:', error?.message)
        }
    }

    const isLoading = createObject.isPending || updateObject.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[1400px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing 
                            ? t('objects.editObject') 
                            : (isService ? t('objects.createService') : t('objects.createObject'))}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('objects.updateObjectInfo')
                            : (isService ? t('objects.createNewServiceDescription') : t('objects.createNewObjectDescription'))}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                            <TabsList
                                className={`grid w-full ${
                                    isService
                                        ? showRunbookTab
                                            ? 'grid-cols-5'
                                            : 'grid-cols-4'
                                        : showRunbookTab
                                            ? 'grid-cols-6'
                                            : 'grid-cols-5'
                                }`}
                            >
                                <TabsTrigger value="details" className="flex items-center gap-2">
                                    <BookOpen className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.objectDetails')}
                                </TabsTrigger>
                                {!isService && (
                                    <TabsTrigger value="floorPlan" className="flex items-center gap-2">
                                        <Map className="h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.floorPlan')}
                                    </TabsTrigger>
                                )}
                                <TabsTrigger value="recoveryInfo" className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.recoveryInformation')}
                                </TabsTrigger>
                                <TabsTrigger value="recoveryDocVideo" className="flex items-center gap-2">
                                    <Video className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.recoveryDocumentVideo')}
                                </TabsTrigger>
                                {showRunbookTab && (
                                    <TabsTrigger value="runbook" className="flex items-center gap-2">
                                        <List className="h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.runbook')}
                                    </TabsTrigger>
                                )}
                                <TabsTrigger value="objectPicture" className="flex items-center gap-2">
                                    <Image className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.objectPicture')}
                                </TabsTrigger>
                            </TabsList>

                            <div className="min-h-[600px] mt-4">
                                <TabsContent value="details" className="mt-0">
                                    <ObjectDetailsTab form={form} isEditing={isEditing} isService={isService} />
                                </TabsContent>

                                {!isService && (
                                    <TabsContent value="floorPlan" className="mt-0">
                                        <FloorPlanTab form={form} />
                                    </TabsContent>
                                )}

                                <TabsContent value="recoveryInfo" className="mt-0">
                                    <RecoveryInformationTab form={form} />
                                </TabsContent>

                                <TabsContent value="recoveryDocVideo" className="mt-0">
                                    <RecoveryDocumentVideoTab form={form} />
                                </TabsContent>

                                {showRunbookTab && (
                                    <TabsContent value="runbook" className="mt-0">
                                        <RunbookTab form={form} />
                                    </TabsContent>
                                )}

                                <TabsContent value="objectPicture" className="mt-0">
                                    <ObjectPictureTab form={form} />
                                </TabsContent>
                            </div>
                        </Tabs>

                        <div className="flex justify-end space-x-2 pt-4 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('common.back')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? (isEditing ? t('objects.updating') : t('objects.creating'))
                                    : t('common.save')}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
